// components/Cards/DanceStyleCard.tsx
import React from 'react';
import { Card, Title, Text, Stack, Group, ActionIcon, Image, Skeleton, Box } from '@mantine/core';
import { Link } from '@inertiajs/react';
import { IconCalendar, IconCalendarOff, IconUsers, IconArrowRight, IconEdit } from '@tabler/icons-react';
import baseImage from '../../../assets/images/BASE.jpg';

interface DanceStyle {
  id: number;
  name: string;
  slug: string;
  cover_image_url?: string;
  upcoming_events_count: number;
  past_events_count: number;
  artists_count: number;
}

interface DanceStyleCardProps {
  danceStyle: DanceStyle;
  canEdit: boolean;
  onCardClick?: (danceStyle: DanceStyle) => void;
}

const DanceStyleCard: React.FC<DanceStyleCardProps> = ({ danceStyle, canEdit, onCardClick }) => {
  return (
    <Card
      key={danceStyle.id}
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      h="100%"
      style={{
        backgroundColor: '#3d3c3e',
        borderColor: '#5c5c5c',
        overflow: 'hidden',
      }}
      onClick={onCardClick ? () => onCardClick(danceStyle) : undefined}
      sx={onCardClick ? { cursor: 'pointer' } : undefined}
    >
      {/* 16:9 Aspect Ratio Container with Centered Text Overlay */}
      <div style={{ width: '100%', paddingTop: '56.25%', position: 'relative' }}>
        <Image
          src={danceStyle.cover_image_url || baseImage}
          alt={danceStyle.name}
          placeholder={<Skeleton height="100%" radius={0} animate />}
          className="ds-image-top"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          radius={0}
        />

        {/* Centered name overlay with gradient background */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7))',
            padding: '0 10px',
            textAlign: 'center',
          }}
        >
          <Title
            order={3}
            className="rubik uppercase"
            style={{
              color: '#ffffff',
              textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
              wordBreak: 'break-word',
              textTransform: 'capitalize',
              fontSize: '1rem',
              lineHeight: '1',
            }}
          >
            {danceStyle.name}
          </Title>
        </Box>
      </div>

      {/* Content Container */}
      <div style={{ padding: '12px' }}>
        <Stack h="100%" justify="space-between" gap="xs">
          <div>
            <Group gap="sm">
              <IconCalendar size={16} style={{ color: '#EA580C' }} />
              <Text size="sm" className="poppins" style={{ color: '#d1d1d1' }}>
                Upcoming: {danceStyle.upcoming_events_count}
              </Text>
            </Group>

            <Group gap="sm">
              <IconCalendarOff size={16} style={{ color: '#EA580C' }} />
              <Text size="sm" className="poppins" style={{ color: '#d1d1d1', opacity: 0.5 }}>
                Past: {danceStyle.past_events_count}
              </Text>
            </Group>

            <Group gap="sm">
              <IconUsers size={16} style={{ color: '#EA580C' }} />
              <Text size="sm" className="poppins" style={{ color: '#d1d1d1' }}>
                Artists: {danceStyle.artists_count}
              </Text>
            </Group>
          </div>
        </Stack>
      </div>
    </Card>
  );
};

export default DanceStyleCard;
